import React from "react";
import styled from "styled-components";
import Bell02 from "@untitled-ui/icons-react/build/esm/Bell02";
import { Tooltip } from "antd";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/notification-del.svg";
import { ReactComponent as ReadIcon } from "../../../assets/icons/notification-read.svg";
import NotificationSkeleton from "../loaders/NotificationSkeleton";

const StyledPage = styled.div`
    & {
        display: flex;
        width: 320px;
        height: auto;
        padding: 12px 8px;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        gap: 10px;

        border-radius: 4px;
        background: var(--backgrounds-lines-n-500, #141922);

        /* drop-shadow-100 */
        box-shadow: 0px 0px 10px 0px #111620;
    }

    .NotificationItemsContainer {
        display: flex;
        flex-direction: column;
        justify-content: start;
        align-items: flex-start;
        align-self: stretch;
        height: auto;
    }

    .Box {
        width: 26px;
        height: 16px;
        position: relative;
    }

    .Icon {
        width: 13.33px;
        height: 12px;
        left: 1.33px;
        top: 2px;
        position: absolute;
        border: 1px #d1d1d1 solid;
    }

    .NotificationItem {
        align-self: stretch;
        padding: 8px;
        border-radius: 4px;
        justify-content: flex-start;
        align-items: center;
        gap: 8px;
        display: inline-flex;
        border-radius: 0;

        border-bottom: 1px solid rgba(60, 72, 90, 0.26);
    }
    .NotificationItem.hover-allowed {
        cursor: pointer;
        &:hover .action-group-btn {
            display: flex;
        }
    }
    .NotificationItem:last-child {
        border-bottom: none;
    }

    .NotificationItemTexts {
        flex: 1 1 0;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 4px;
        display: inline-flex;
    }

    .TitleText {
        color: var(--text-icons-gray-100, #d1d1d1);

        /* Subtitle 2 */
        font-family: Roboto;
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .DescriptionText {
        color: var(--text-icons-gray-200, #7c868b);
        text-align: right;
        width: 65px;
        /* Label/XS-Lowercase */
        font-family: Roboto;
        font-size: 0.75rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .NotReadedEllipse {
        width: 8px;
        height: 8px;
        background: #ff9900;
        border-radius: 9999px;
        position: absolute;
        left: 13px;
        top: -2px;
    }

    .NotificationItem:hover {
        background: #1e2631;
    }

    .BtnLargeSolid {
        align-self: stretch;
        height: 35px;
        padding: 10px;
        background: #1e2631;
        border-radius: 4px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        display: inline-flex;
        cursor: pointer;
    }
    .BtnLargeSolid:hover {
        border: 1px solid #00bb00;
    }

    .Button {
        color: #d1d1d1;
        font-size: 10px;
        font-family: Roboto;
        font-weight: 400;
        text-transform: uppercase;
        letter-spacing: 0.12px;
        word-wrap: break-word;
    }
    .text-gray-100 {
        position: absolute;
    }
    .action-group-btn {
        height: 24px;
        width: 44px;
        background-color: #141922;
        border-radius: 5px;
        padding: 6px;
        display: none; /* Initially hidden */
        flex-direction: row;
        gap: 8px;
        justify-content: center;
        align-items: center;
        position: absolute;
        right: 10px;
    }
    .action-group-btn div {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .action-group-btn div.delete:hover svg path {
        stroke: #ff3636; /* Change stroke color on hover */
    }
    .action-group-btn div.read:hover svg path {
        stroke: #00bb00; /* Change stroke color on hover */
    }
    .NotificationItem--No {
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        width: 100%;
    }
    .NotificationItemsContainer.no-notifications {
        justify-content: center;
        height: 254px !important;
    }
`;

/**
 * Component for displaying a dropdown of notifications.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.NotificationItems - The array of notification items to display.
 * @param {String} props.NotificationItems.title - The title of the notification.
 * @param {String} props.NotificationItems.description - The description of the notification.
 * @param {Boolean} props.NotificationItems.unread - The flag to indicate if the notification is unread.
 * @param {Boolean} props.isLoadingNotifications - The flag to indicate if the notifications are loading.
 * @param {Object} props.style - The inline style object for the component.
 * @param {Function} props.handleReadNotification - Function to handle marking a notification as read.
 * @param {Function} props.handleDeleteNotification - Function to handle deleting a notification.
 * @returns {JSX.Element} - The rendered component.
 */
export default function NotificationsDropdown(props) {
    const { NotificationItems } = props;

    return (
        <StyledPage className={props?.className ?? ""} style={props?.style}>
            <div className="TitleText" style={{ fontSize: "20px" }}>
                Notifications
            </div>
            {props?.isLoadingNotifications ? (
                <NotificationSkeleton count={5} />
            ) : (
                <div
                    className={`NotificationItemsContainer ${!NotificationItems || NotificationItems?.length === 0 ? "no-notifications" : ""}`}>
                    {(!NotificationItems || NotificationItems?.length === 0) && (
                        <div className="NotificationItem--No">
                            <div className="">
                                <div className="TitleText">There are no notifications</div>
                            </div>
                        </div>
                    )}
                    {NotificationItems.map((item, index) => (
                        <div
                            key={index}
                            className="NotificationItem hover-allowed"
                            onClick={() => props?.handleDestinationPage(item?.destinationPageURL)}>
                            <div className="Box">
                                <Bell02 className="text-gray-100" height="16px" />
                                <div className="NotReadedEllipse"></div>
                            </div>
                            <div className="NotificationItemTexts">
                                <div className="TitleText">{item?.description}</div>
                                <div className="DescriptionText">{item?.createdAt}</div>
                                <div className="action-group-btn">
                                    {/* Delete button */}
                                    <Tooltip
                                        placement="top"
                                        title={"DELETE"}
                                        color="#212121"
                                        overlayClassName="custom-tool-tip">
                                        <div
                                            className="delete"
                                            onClick={e => {
                                                e.stopPropagation(); // Prevent triggering parent onClick
                                                props?.handleDeleteNotification(item?.id);
                                            }}>
                                            <DeleteIcon />
                                        </div>
                                    </Tooltip>
                                    {/* Mark as read button */}
                                    <Tooltip
                                        placement="top"
                                        title={"MARK AS READ"}
                                        color="#212121"
                                        overlayClassName="custom-tool-tip">
                                        <div
                                            className="read"
                                            onClick={e => {
                                                e.stopPropagation(); // Prevent triggering parent onClick
                                                props?.handleReadNotification(item?.id);
                                            }}>
                                            <ReadIcon />
                                        </div>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div
                className="BtnLargeSolid"
                onClick={() => {
                    props?.setShowNotificationPanel(true);
                    props?.setShowNotificationDropdown(false);
                    props?.getUnreadNotifications();
                }}>
                <div className="Button">Show all notifications</div>
            </div>
        </StyledPage>
    );
}
